import { FC, useEffect, useRef, useState } from "react";
import { Tag as TagIcon, X } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "../../ui/popover";
import { cn } from "../../utils";
import { TagOption, TagsInputProps } from "./types";
import { Input } from "../../ui/input";
import { TagItem, TagSuggestionItem } from "./parts";
import { KEYBOARD_KEYS } from "../../consts/keyboard";
import { normalizeToId } from "./utils";

export const TagsInput: FC<TagsInputProps> = ({
  value = [],
  variant = "neutral",
  placeholder = "Type to add a tag...",
  deleteText = "Delete",
  suggestions = [],
  allowCreate = true,
  maxTags = 100,
  disabled = false,
  className,
  suggestionWrapperClassName,
  suggestionClassName,
  tagClassName,
  tagInputIcon,
  onDeleteSuggestion,
  onBlur,
  normalizeTags: propsNormalizeTags = normalizeToId,
  onChange = () => {},
  onAddTag: propsOnAddTag = () => {},
  onRemoveTag: propsOnRemoveTag = () => {},
  onResetTags: propsOnResetTags = () => {},
}) => {
  const [inputValue, setInputValue] = useState("");
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const tagsRef = useRef<HTMLDivElement>(null);
  const [triggerWidth, setTriggerWidth] = useState<number>();

  useEffect(() => {
    // Update width to match the trigger width
    if (triggerRef.current) {
      setTriggerWidth(triggerRef.current.offsetWidth);
    }
  }, []);

  const isTagAddingDisabled = value.length >= maxTags;

  const onAddTag = (newTag: TagOption) => {
    // Prevent adding more tags than maxTags
    if (isTagAddingDisabled) return;
    // Prevent adding duplicate tags
    if (value.some(tag => tag.id === newTag.id)) return;

    const updatedTags = [...value, newTag];

    onChange(updatedTags);
    propsOnAddTag(newTag);
  };

  const onRemoveTag = (id: string) => {
    const updatedTags = value.filter(tag => tag.id !== id);

    onChange(updatedTags);
    propsOnRemoveTag(id);
  };

  const onResetTags = () => {
    onChange?.([]);
    propsOnResetTags();
  };

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    setOpen(true);
  };

  const filteredSuggestions = (): TagOption[] => {
    const trimmedInputValue = inputValue.trim().toLowerCase();
    if (!trimmedInputValue) return [] as TagOption[];
    const setIds = new Set(value.map(t => t.id));

    // Filter suggestions based on input value and exclude already selected tags
    return suggestions.filter(
      suggestion =>
        suggestion.label.toLowerCase().includes(trimmedInputValue) &&
        !setIds.has(suggestion.id),
    );
  };

  const normalizeTags = (input: string) => {
    if (propsNormalizeTags) return propsNormalizeTags(input);
    return input;
  };

  const onCommit = () => {
    const trimmedInputValue = inputValue.trim();
    if (!trimmedInputValue) return;

    // Normalize the input value to create a valid tag id
    const normalizedId = normalizeTags(trimmedInputValue);
    if (!normalizedId) return;

    // If the tag already exists in the selected value, reuse silently
    if (value.some(tag => tag.id === normalizedId)) {
      setInputValue("");
      setOpen(false);
      return;
    }

    // Try to find a matching suggestion by normalized id or id
    const foundSuggestion = suggestions.find(
      suggestion =>
        suggestion.id === normalizedId ||
        normalizeTags(suggestion.label) === normalizedId,
    );

    if (foundSuggestion) {
      onAddTag(foundSuggestion);
    } else if (allowCreate) {
      // Create a new tag using normalized id as id and label
      onAddTag({ id: normalizedId, label: normalizedId });
    }

    // Reset input
    setInputValue("");
    setOpen(false);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const isRemoveTagPressed =
      e.key === KEYBOARD_KEYS.BACKSPACE && !inputValue && value.length > 0;
    const isAddTagPressed = e.key === KEYBOARD_KEYS.ENTER && inputValue;
    const isEscapePressed = e.key === KEYBOARD_KEYS.ESCAPE;

    if (isAddTagPressed) {
      e.preventDefault();
      onCommit();
      if (tagsRef.current) {
        // Scroll to the end to show the newly added tag
        tagsRef.current.scrollLeft = tagsRef.current.scrollWidth;
      }
    } else if (isRemoveTagPressed) {
      onRemoveTag(value[value.length - 1].id);
    } else if (isEscapePressed) {
      setOpen(false);
    }
  };

  const renderSuggestionsIfNeeded = () => {
    if (inputValue && filteredSuggestions().length > 0)
      return (
        <PopoverContent
          className="p-0 border-transparent bg-fill-interactive-neutral-idle gap-2"
          align="start"
          style={{ width: triggerWidth ?? undefined }}
          onOpenAutoFocus={e => e.preventDefault()}
          onCloseAutoFocus={e => e.preventDefault()}
          onMouseDown={e => e.preventDefault()}
        >
          <div
            className={cn(
              "max-h-50 overflow-y-auto flex flex-col gap-1 p-1",
              suggestionWrapperClassName,
            )}
          >
            {filteredSuggestions().map(suggestion => (
              <TagSuggestionItem
                key={suggestion.id}
                label={suggestion.label}
                id={suggestion.id}
                onDeleteSuggestion={onDeleteSuggestion}
                deleteText={deleteText}
                className={suggestionClassName}
                tagClassName={tagClassName}
                tagVariant={variant}
                onClick={() => {
                  onAddTag(suggestion);
                  setInputValue("");
                  setOpen(false);
                  inputRef.current?.focus();
                }}
              />
            ))}
          </div>
        </PopoverContent>
      );
    return undefined;
  };

  const renderTagsInputIcon = () =>
    tagInputIcon || <TagIcon className="size-4 stroke-icon-bg-neutral-mild" />;

  const renderTags = () =>
    value.map(tag => (
      <TagItem
        key={tag.id}
        label={tag.label}
        id={tag.id}
        onRemoveTag={onRemoveTag}
        disabled={disabled}
        className={tagClassName}
        variant={variant}
      />
    ));

  const shouldRenderResetButtonIfNeeded = () => {
    if (value.length === 0) return <></>;

    return (
      <X
        className="size-4 hover:cursor-pointer stroke-icon-input-neutral-idle hover:stroke-icon-input-neutral-hover"
        onClick={onResetTags}
      />
    );
  };

  return (
    <div className={cn("w-full", className)}>
      <Popover open={open && !disabled} onOpenChange={setOpen} modal={false}>
        <PopoverTrigger className="w-full">
          <div
            ref={triggerRef}
            tabIndex={-1}
            className={cn(
              "flex items-center gap-2 border border-border-tag-neutral-idle bg-purple-muted-800 py-0.5 px-3 rounded-md scrollbar-thin",
              { "opacity-60": disabled },
            )}
            onClick={e => {
              e.stopPropagation();
              setOpen(true);
              inputRef.current?.focus();
            }}
          >
            {renderTagsInputIcon()}
            <div
              ref={tagsRef}
              className="flex items-center gap-2 w-full whitespace-normal overflow-x-auto"
            >
              {renderTags()}
              <Input
                ref={inputRef}
                value={inputValue}
                onChange={onInputChange}
                onClick={e => e.stopPropagation()}
                onFocus={() => setOpen(true)}
                onBlur={onBlur}
                onKeyDown={onKeyDown}
                placeholder={placeholder}
                disabled={disabled || isTagAddingDisabled}
                className={cn(
                  "m-0 h-6.5 flex-1 min-w-24 border-0 bg-transparent p-0 shadow-none focus-visible:outline-none focus-visible:ring-0 font-normal text-xs",
                  "placeholder:text-text-input-neutral-placeholder placeholder:text-xs",
                )}
              />
            </div>
            {shouldRenderResetButtonIfNeeded()}
          </div>
        </PopoverTrigger>
        {renderSuggestionsIfNeeded()}
      </Popover>
    </div>
  );
};
